/**
 * MagneticText - Text that follows cursor within bounds
 *
 * Creates a magnetic hover effect where text subtly follows
 * the cursor position, snapping back elastically on leave.
 */

import { useRef, useCallback, type ReactNode, type CSSProperties } from 'react'

interface MagneticTextProps {
  children: ReactNode
  className?: string
  style?: CSSProperties
  strength?: number
  as?: 'span' | 'div' | 'a' | 'button'
  href?: string
  onClick?: () => void
}

export function MagneticText({
  children,
  className = '',
  style,
  strength = 0.3,
  as: Component = 'span',
  href,
  onClick,
}: MagneticTextProps) {
  const elementRef = useRef<HTMLElement>(null)
  const innerRef = useRef<HTMLSpanElement>(null)

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      const element = elementRef.current
      const inner = innerRef.current
      if (!element || !inner) return

      // Check for reduced motion preference
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return
      }

      const rect = element.getBoundingClientRect()
      const centerX = rect.left + rect.width / 2
      const centerY = rect.top + rect.height / 2

      const deltaX = (e.clientX - centerX) * strength
      const deltaY = (e.clientY - centerY) * strength

      inner.style.transform = `translate(${deltaX}px, ${deltaY}px)`
      inner.style.transition = 'transform 0.15s ease-out'
    },
    [strength]
  )

  const handleMouseLeave = useCallback(() => {
    const inner = innerRef.current
    if (!inner) return

    inner.style.transform = 'translate(0, 0)'
    inner.style.transition = 'transform 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)'
  }, [])

  const props = {
    ref: elementRef as any,
    className: `magnetic-wrap ${className}`,
    style: { display: 'inline-block', ...style },
    onMouseMove: handleMouseMove,
    onMouseLeave: handleMouseLeave,
    ...(href && { href }),
    ...(onClick && { onClick }),
  }

  return (
    <Component {...props}>
      <span ref={innerRef} style={{ display: 'inline-block' }}>
        {children}
      </span>
    </Component>
  )
}

export default MagneticText
